import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  BET_TIMEOUT,
  betStatus,
  clearBetStatusResponse,
  fetchBalance,
  fetchButtonVariables,
  setBettingInprogress,
  setCashoutInProgress,
  setIsOneClickBettingEnabled,
  setIsOneClickBettingLoading,
  setOneClickBettingStakeAmount,
  showToast,
  useAppDispatch,
  useAppSelector,
} from "@hypexone/uicore";

export const DEFAULT_ONE_CLICK_STAKES = [100, 500, 1000, 5000];
const ONE_CLICK_STATUS_POLL = 2000;

type OneClickPrefs = {
  enabled?: boolean;
  stakes?: number[];
  stake?: number;
};

// Kept per user, like the multi-market list, so a shared browser doesn't
// carry one user's one-click choice over to another.
const prefsKey = () => `oneClick_${sessionStorage.getItem("username") ?? ""}`;

export const readOneClickPrefs = (): OneClickPrefs => {
  try {
    return JSON.parse(localStorage.getItem(prefsKey()) ?? "{}") ?? {};
  } catch {
    return {};
  }
};

export const saveOneClickPrefs = (prefs: OneClickPrefs) => {
  try {
    localStorage.setItem(
      prefsKey(),
      JSON.stringify({ ...readOneClickPrefs(), ...prefs })
    );
  } catch {
    // Storage blocked: one-click still works, it just isn't remembered.
  }
};

/** The user turning one-click on or off; remembered across pages. */
export const toggleOneClickBetting = (
  dispatch: (action: unknown) => unknown,
  enabled: boolean
) => {
  saveOneClickPrefs({ enabled });
  dispatch(setIsOneClickBettingEnabled(enabled));
};

/**
 * The stakes offered in the one-click bar: the user's own edited set, else
 * the first four of their saved stake buttons, else the defaults.
 */
export const useOneClickStakes = () => {
  const dispatch = useAppDispatch();
  const { loggedIn } = useAppSelector((state) => state.auth);
  const { buttonVariables, oneClickBettingStakeAmount } = useAppSelector(
    (state) => state.betSlip
  );

  useEffect(() => {
    if (loggedIn && !buttonVariables?.length) {
      dispatch(fetchButtonVariables());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  const saved = readOneClickPrefs();
  const fromButtons = (buttonVariables ?? [])
    .map((button) => Number(button?.stake))
    .filter((stake) => stake > 0)
    .slice(0, 4);
  const stakes = saved.stakes?.length
    ? saved.stakes
    : fromButtons.length
    ? fromButtons
    : DEFAULT_ONE_CLICK_STAKES;

  // The stake the user picked last, if it's still offered; else the first.
  useEffect(() => {
    if (stakes.includes(oneClickBettingStakeAmount)) return;
    dispatch(
      setOneClickBettingStakeAmount(
        stakes.includes(saved.stake) ? saved.stake : stakes[0]
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stakes.join(",")]);

  const selectStake = (stake: number) => {
    saveOneClickPrefs({ stake });
    dispatch(setOneClickBettingStakeAmount(stake));
  };

  const saveStakes = (next: number[]) => {
    if (!next.length) return;
    saveOneClickPrefs({ stakes: next, stake: next[0] });
    dispatch(setOneClickBettingStakeAmount(next[0]));
  };

  return { stakes, selectStake, saveStakes };
};

/**
 * Leaving an event page switches one-click off (uicore resets it on unmount);
 * turn it back on if the user left it on, as Sky keeps it.
 */
export const useRestoreOneClickBetting = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { loggedIn } = useAppSelector((state) => state.auth);
  const { isOneClickBettingEnabled } = useAppSelector(
    (state) => state.betSlip
  );

  useEffect(() => {
    if (loggedIn && !isOneClickBettingEnabled && readOneClickPrefs().enabled) {
      dispatch(setIsOneClickBettingEnabled(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, loggedIn]);
};

/**
 * Bet result for one-click bets placed outside an event page. Nothing pushes
 * the result there, so poll /bet-status once the bet is sent, then clear the
 * in-progress flag that otherwise blocks every later click.
 */
export const useOneClickBetResult = () => {
  const dispatch = useAppDispatch();
  const { isOneClickBettingLoading, betStatusResponse } = useAppSelector(
    (state) => state.betSlip
  );
  const startedAt = useRef(0);

  useEffect(() => {
    if (!isOneClickBettingLoading) return;
    startedAt.current = Date.now();
    const poll = setInterval(
      () => dispatch(betStatus()),
      ONE_CLICK_STATUS_POLL
    );
    return () => clearInterval(poll);
  }, [isOneClickBettingLoading]);

  useEffect(() => {
    if (betStatusResponse === null) return;

    const { status, message } = betStatusResponse;
    // Still being matched: keep polling quietly until BET_TIMEOUT.
    if (
      status === "IN_PROGRESS" &&
      Date.now() - startedAt.current < BET_TIMEOUT
    ) {
      dispatch(clearBetStatusResponse());
      return;
    }
    dispatch(
      showToast({
        type: status === "SUCCESS" ? "success" : "error",
        message: status === "FAIL" ? "Bet failed - " + message : message,
      })
    );
    if (status === "SUCCESS") {
      dispatch(fetchBalance());
    }

    dispatch(setBettingInprogress(false));
    dispatch(setCashoutInProgress(null));
    dispatch(setIsOneClickBettingLoading(false));
    dispatch(clearBetStatusResponse());
  }, [betStatusResponse]);
};
